<template>
	<div>
		<div
			class="modal-container modal-info modal-large modal-help"
			:style="{ zIndex: '9999999999' }"
		>
			<div class="modal-header">
				<span>{{ row?.fields?.summary }}</span>
				<span class="header-info">
					<span v-if="row?.fields?.customfield_11049.trim() != 'N/A'">Deal#: {{ row?.fields?.customfield_11049 }}</span>
					<span v-if="row?.fields?.customfield_11054.trim() != 'N/A'">Store: {{ row?.fields?.customfield_11054 }}</span>
				</span>
			</div>
			<IsBusySectionComponent v-if="isBusy" />
			<div
				v-else
				class="modal-body"
			>
				<div class="comment-text">
					<Comment
						:comment="getDescription()"
						:author="'Automation for Jira'"
						:date="row?.fields?.created || ''"
					/>
				</div>
				<div
					v-for="c in comment"
					:key="c"
					class="comment-text"
				>
					<Comment
						:comment="c.text"
						:author="c.author"
						:date="c.timestamp"
					/>
				</div>
				<div class="comment-text">
					<InputTextbox
						label="Reply"
						v-model:saturn="reply"
						:multiline="true"
						:rows="6"
					/>
				</div>
			</div>
			<div class="modal-footer">
				<button
					class="button-save"
					:disabled="isBusy"
					@click="close()"
					style="background-color: var(--error-color)"
				>
					Close
				</button>
				<button
					class="button-save"
					@click="sendReply"
					:disabled="!reply?.trim()"
				>
					Send
				</button>
			</div>
		</div>
	</div>
</template>
<script lang="ts">
	export interface IModalHelpInfoProps {
		modal: {
			passedData: {
				row: any;
			};
		};
	}
</script>
<script setup lang="ts">
	import { ref, onMounted } from 'vue';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import Comment from '@/components/Comment.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import IsBusySectionComponent from '@core/components/IsBusySectionComponent.vue';
	import util from '@core/services/util';

	const props = defineProps<IModalHelpInfoProps>();
	const row = ref<any>(props.modal.passedData.row);
	const isBusy = ref(false);
	const comment = ref<any>(null);
	const reply = ref<any>(null);
	const reloadTable = ref(false);

	onMounted(() => {
		comments();
	});

	const close = () => {
		$modal.close(reloadTable.value);
	};

	const sendReply = async () => {
		isBusy.value = true;
		await api.utilities.sendHelpReply({
			issueId: row.value.key,
			reply: reply.value,
		});
		util.toastr('success', 'Reply sent', 'Your reply has been sent to the support team');
		comment.value.push({
			text: reply.value,
			timestamp: new Date().toISOString(),
			author: 'Automation for Jira',
		});
		isBusy.value = false;
		reply.value = null;
		reloadTable.value = true;
	};

	// wrangle usable comment strings out of very nested jira comment objects
	const comments = (): any => {
		if (!row.value?.fields?.comment?.comments) return [];
		comment.value = row.value.fields.comment.comments
			.map((comment: any) => {
				const isAutomationUser = comment.author.displayName === 'Automation for Jira';
				// Find panel elements with type "info"
				const infoPanels = comment.body?.content?.filter((content: any) => content.type === 'panel' && content.attrs?.panelType === 'info') || [];

				// For Automation user, process all comments. For others, only process if they have info panels
				if (!isAutomationUser && infoPanels.length === 0) return null;

				let text = '';
				if (infoPanels.length > 0) {
					// Process info panels
					infoPanels.forEach((panel: any) => {
						panel.content?.forEach((content: any) => {
							if (content.type === 'paragraph') {
								content.content?.forEach((item: any) => {
									if (item.type === 'text') {
										text += item.text;
									} else if (item.type === 'hardBreak') {
										text += '\n';
									}
								});
								text += '\n';
							}
						});
					});
				} else if (isAutomationUser) {
					// Process regular content for Automation user
					comment.body?.content?.forEach((content: any) => {
						if (content.type === 'paragraph') {
							content.content?.forEach((item: any) => {
								if (item.type === 'text') {
									text += item.text;
								} else if (item.type === 'hardBreak') {
									text += '\n';
								}
							});
							text += '\n';
						}
					});
				}

				const trimmedText = text.trim();
				if (!trimmedText) return null;

				return {
					text: trimmedText,
					timestamp: comment.created,
					author: comment.author.displayName,
				};
			})
			.filter((comment: any) => comment !== null);
	};

	const getDescription = (): string => {
		if (!row.value?.fields?.description?.content) return '';
		let text = '';
		let foundSolutions = false;

		row.value.fields.description.content.forEach((content: any) => {
			if (content.type === 'paragraph') {
				let paragraphText = '';
				content.content?.forEach((item: any) => {
					if (item.type === 'text') {
						paragraphText += item.text;
					} else if (item.type === 'hardBreak') {
						paragraphText += '\n';
					}
				});

				if (foundSolutions) {
					text += paragraphText + '\n';
				} else if (paragraphText.includes('Solutions')) {
					foundSolutions = true;
					// Get everything after "Solutions"
					const afterSolutions = paragraphText.split('Solutions')[1];
					if (afterSolutions) {
						text += afterSolutions + '\n';
					}
				}
			}
		});

		return text.trim() || 'No description provided';
	};
</script>
<style scoped>
	.comment-text {
		white-space: pre-wrap;
		word-break: break-word;
		width: 100%;
	}

	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.header-info {
		display: flex;
		gap: 15px;
		font-size: 0.9em;
		color: var(--text-color-secondary);
	}
</style>
